import {UploadIntent} from "@/domain/uploader/scheduler";
import Vue from "vue";
import fileUtils from "@/domain/core/utils/fileUtils";
import MqttClient from "@/domain/core/utils/mqttClient";

const state = {
    config: {
        media_upload_left: 0,
        is_advanced_available: false,
    },
    toUpload: [],
    inProgress: [],
    completed: [],
    failed: [],
    photographerId: null,
    picaServiceId: null,
    galleryId: null,
    scheduler: null,
    pending: [],
    availableTags: {},
    availableGalleries: [],
    stats: null,
    forceToPublic: false,
}

export const getters = {
    media_upload_left: (state) => {
        let left = state.config.media_upload_left
        if (left >= 9999) return left
        left -= state.toUpload.length
        left -= state.inProgress.length
        left -= state.completed.length
        return left
    },
    toUpload: state => state.toUpload,
    inProgress: state => state.inProgress,
    completed: state => state.completed,
    failed: state => state.failed,
    photographerId: state => state.photographerId,
    picaServiceId: state => state.picaServiceId,
    galleryId: state => state.galleryId,
    availableTags: state => state.availableTags,
    availableGalleries: state => state.availableGalleries,
    pending: state => {
        return state.pending.filter(file => !state.toUpload.find(f => f.md5 === file.md5))
            .filter(file => !state.inProgress.find(f => f.md5 === file.md5))
            .filter(file => !state.completed.find(f => f.md5 === file.md5))
            .filter(file => !state.failed.find(f => f.md5 === file.md5))
    },
    stats: state => state.stats,
}


export const mutations = {
    setConfig(state, config) {
        state.config = config
    },
    addToToUpload(state, files) {
        // The file should not be already in the toUpload, in progress or completed
        files = files.filter(file => !state.toUpload.find(f => f.md5 === file.md5))
        files = files.filter(file => !state.inProgress.find(f => f.md5 === file.md5))
        files = files.filter(file => !state.completed.find(f => f.md5 === file.md5))

        // If the file was in the pending queue, we should recover all the "info" associated with it
        files = files.map(file => {
            const pending = state.pending.find(f => f.md5 === file.md5)
            if (!pending) {
                file.public = state.forceToPublic
                return file
            }

            file.tags = pending.tags
            file.free = pending.free
            file.disableAutotag = pending.disableAutotag
            file.public = pending.public || state.forceToPublic
            file.picaCode = pending.picaCode
            file.gallery = pending.gallery
            return file
        })

        // We can add the elements to the toUpload
        state.toUpload.push(...files)

        // If a file is in the failed toUpload, it should be removed since we are trying to upload it again
        state.failed = state.failed.filter(file => !files.find(f => f.md5 === file.md5))
    },
    delFromToUpload(state, files) {
        state.toUpload = state.toUpload.filter(f => !files.find(file => file.md5 === f.md5))
    },
    updateToUploadTag(state, {mode, tags, files}) {
        const selectedFiles = files.reduce((acc, file) => {
            acc[file.md5] = true
            return acc
        }, {})

        state.toUpload = state.toUpload.map(file => {
            if (!selectedFiles[file.md5]) return file
            if (mode === 'delete')
                file.tags = []
            else {
                for (let i = 0; i < tags.length; i++) {
                    if (file.tags.includes(tags[i]))
                        continue
                    file.tags.push(tags[i])
                }
            }
            return file
        })
    },
    updateToUploadPicaCode(state, {picaCode, files}) {
        const selectedFiles = files.reduce((acc, file) => {
            acc[file.md5] = true
            return acc
        }, {})

        state.toUpload = state.toUpload.map(file => {
            if (!selectedFiles[file.md5]) return file
            file.picaCode = picaCode
            return file
        })
    },
    updateToUploadGallery(state, {gallery, files}) {
        const selectedFiles = files.reduce((acc, file) => {
            acc[file.md5] = true
            return acc
        }, {})

        state.toUpload = state.toUpload.map(file => {
            if (!selectedFiles[file.md5]) return file
            file.gallery = gallery
            return file
        })
    },
    updateToUploadFlag(state, {flag, value, files}) {
        const selectedFiles = files.reduce((acc, file) => {
            acc[file.md5] = true
            return acc
        }, {})

        state.toUpload = state.toUpload.map(file => {
            if (!selectedFiles[file.md5]) return file
            if (value)
                file[flag] = true
            else
                delete file[flag]
            return file
        })
    },

    addToInProgress(state, files) {
        state.inProgress.push(...files)
    },
    delFromInProgress(state, files) {
        state.inProgress = state.inProgress.filter(f => !files.find(file => file.md5 === f.md5))
    },
    updateFileProgress(state, {md5, progress, speed}) {
        const index = state.inProgress.findIndex(f => f.md5 === md5)
        if (index !== -1) {
            let file = state.inProgress[index]
            file.progress = progress
            file.speed = speed
            Vue.set(state.inProgress, index, file)
        }
    },

    addToCompleted(state, files) {
        state.completed.push(...files)
    },
    delFromCompleted(state, files) {
        state.completed = state.completed.filter(f => !files.find(file => file.md5 === f.md5))
    },

    addToFailed(state, {files, motivation}) {
        if (motivation) {
            files = files.map(file => {
                file.motivation = motivation
                return file
            })
        }
        state.failed.push(...files)
    },
    delFromFailed(state, files) {
        state.failed = state.failed.filter(f => !files.find(file => file.md5 === f.md5))
    },

    setPhotographerId(state, photographerId) {
        state.photographerId = photographerId
    },
    setPicaServiceId(state, picaServiceId) {
        if (state.picaServiceId != picaServiceId)
            state.stats = null
        state.picaServiceId = picaServiceId
    },
    setGalleryId(state, galleryId) {
        state.galleryId = galleryId
    },
    forceToPublic(state, forceToPublic) {
        state.forceToPublic = forceToPublic
    },
    restoreStateFromMemory(state) {
        const storedData = localStorage.getItem('store_uploader')
        if (!storedData) return;
        state.pending = JSON.parse(storedData)
    },
    delFromPending(state, files) {
        state.pending = state.pending.filter(f => !files.find(file => file.md5 === f.md5))
        localStorage.setItem('store_uploader', JSON.stringify(state.pending))
    },
    addToPending(state, files) {
        files = files.filter(file => !state.pending.find(f => f.md5 === file.md5))
        state.pending.push(...files.map(file => {
            return {
                md5: file.md5,
                name: file.name,
                human_size: file.human_size,
                tags: file.tags,
                free: file.free,
                disableAutotag: file.disableAutotag,
                public: file.public,
                picaCode: file.picaCode,
                gallery: file.gallery,
                resolution: file.resolution,
            }
        }))
        localStorage.setItem('store_uploader', JSON.stringify(state.pending))
    },
    updatePendingStorage(state) {
        localStorage.setItem('store_uploader', JSON.stringify(state.pending))
    },
    setAvailableTags(state, tags) {
        state.availableTags = tags
    },
    setAvailableGalleries(state, galleries) {
        state.availableGalleries = galleries
    },
    updateImageResolution(state, {file, resolution}) {
        const updateFile = (files, file, resolution) => {
            const index = files.findIndex(f => f.md5 === file.md5)
            if (index !== -1) {
                let file = files[index]
                file.resolution = resolution
                Vue.set(files, index, file)
            }
        }

        updateFile(state.toUpload, file, resolution)
        updateFile(state.inProgress, file, resolution)
        updateFile(state.completed, file, resolution)
        updateFile(state.failed, file, resolution)
    },
    setStats(state, stats) {
        state.stats = {...state.stats, ...stats}
    },
    setProcessed(state, processed) {
        state.stats.processed = processed
    },
    setUploaded(state, uploaded) {
        state.stats.uploaded = uploaded
    }
}


export const actions = uploaderService => ({
    async fetchConfig({commit, getters, dispatch}) {
        const config = await uploaderService.fetchConfig(getters['picaServiceId'])
        commit('setConfig', config)
        dispatch('login/setPermissions', {
            type: 'uploader',
            permissions: config.permission,
            id: getters['picaServiceId']
        }, {root: true})
    },
    restoreStateFromMemory({commit}) {
        commit('restoreStateFromMemory')
    },
    async addFilesToToUpload({commit, getters, rootGetters}, files) {
        let num_errors = 0
        let media_left = getters['media_upload_left']
        const validationRules = fileUtils.getValidationRules({
            allowed_extensions: rootGetters['login/hasPerm']('uploader.video') ? ['jpg', 'jpeg', 'mp4'] : ['jpg', 'jpeg'],
            max_image_size: 50 * 1024 * 1024, // 50MB
            max_video_size: 300 * 1024 * 1024, // 300MB
        })

        if (files instanceof FileList)
            files = Array.from(files)

        files = await Promise.all(files.map(async (file) => {
            file.url = URL.createObjectURL(file)

            file.md5 = uploaderService.generateMd5(file)
            file.human_size = fileUtils.formatBytes(file.size)
            file.tags = []

            file.resolution = null

            try {
                await fileUtils.validate(file, validationRules)
            } catch (error) {
                const motivation = error instanceof Error ? error.message : error
                commit('addToFailed', {files: [file], motivation: motivation, retry: false})
                num_errors++
                return null
            }

            // Since we have an await with during validation phase, the check for media_left cannot be done at the start
            // otherwise we would have the wrong count, we should wait until everything is checked
            if (media_left <= 0) {
                commit('addToFailed', {
                    files: [file],
                    motivation: 'You have reached the maximum number of media you can upload',
                    retry: false
                })
                num_errors++
                return null
            }
            media_left -= 1
            return file
        }))
        files = files.filter(file => file !== null) // This will eliminate all the null caused by the validateFile function ( already put in the failed category )

        commit('addToToUpload', files)
        commit('addToPending', files)

        return {
            errors: num_errors,
            valid: files.length
        }
    },
    deleteFileFromToUpload({commit}, files) {
        commit('delFromToUpload', files)
        commit('delFromPending', files)
    },
    updateToUploadTag({commit}, {mode, tags, files}) {
        commit('updateToUploadTag', {mode, tags, files})
        commit('updatePendingStorage')
    },
    updateToUploadPicaCode({commit}, {picaCode, files}) {
        commit('updateToUploadPicaCode', {picaCode, files})
        commit('updatePendingStorage')
    },
    updateToUploadGallery({commit}, {gallery, files}) {
        commit('updateToUploadGallery', {gallery, files})
        commit('updatePendingStorage')
    },
    updateToUploadDisableAutotag({commit}, {disabled, files}) {
        commit('updateToUploadFlag', {flag: 'disableAutotag', value: disabled, files})
        commit('updatePendingStorage')
    },
    updateToUploadIsFree({commit}, {isFree, files}) {
        commit('updateToUploadFlag', {flag: 'free', value: isFree, files})
        commit('updatePendingStorage')
    },

    async moveFilesToInProgress({commit, dispatch}, files) {
        commit('delFromToUpload', files)
        await dispatch('prepareAndAddFileToInProgress', files)
    },
    async prepareAndAddFileToInProgress({commit, getters}, files) {
        const intents = await uploaderService.fetchUploadIntent(getters['picaServiceId'], getters['photographerId'], getters['galleryId'], files)
        files = files.filter((file) => {
            if (intents.existing.find(md5 => md5 === file.md5)) {
                commit('addToFailed', {files: [file], motivation: 'Already uploaded'})
                return false
            }

            const payload = intents.payloads.find(intent => intent.md5 === file.md5)
            if (!payload) {
                commit('addToFailed', {files: [file], motivation: 'No upload intent provided for this file'})
                return false
            }

            file.intent = new UploadIntent(intents.url, payload)
            return true
        })
        commit('addToInProgress', files)
    },
    deleteFileFromInProgress({commit}, files) {
        commit('delFromInProgress', files)
        commit('delFromPending', files)
    },
    updateFileProgress({commit}, {md5, progress, speed}) {
        commit('updateFileProgress', {md5, progress, speed})
    },
    async moveFilesToCompleted({commit}, files) {
        commit('delFromInProgress', files)
        commit('addToCompleted', files)
        commit('delFromPending', files)
    },
    deleteFromCompleted({commit}, files) {
        commit('delFromCompleted', files)
    },

    async moveFilesToFailed({commit}, {files, motivation}) {
        commit('delFromInProgress', files)
        commit('addToFailed', {files: files, motivation: motivation})
    },
    deleteFromFailed({commit}, files) {
        commit('delFromFailed', files)
        commit('delFromPending', files)
    },

    deleteFromPending({commit}, files) {
        commit('delFromPending', files)
    },

    setPhotographerId({commit}, photographerId) {
        commit('setPhotographerId', photographerId)
    },
    setPicaServiceId({commit}, picaServiceId) {
        commit('setPicaServiceId', picaServiceId)
    },
    setGalleryId({commit}, galleryId) {
        commit('setGalleryId', galleryId)
    },
    setForceToPublic({commit}, forceToPublic) {
        commit('forceToPublic', forceToPublic)
    },
    async fetchAvailableTags({commit, getters}) {
        const tags = await uploaderService.fetchAvailableTags(getters['picaServiceId'])
        commit('setAvailableTags', tags)
    },
    async fetchAvailableGalleries({commit, getters}) {
        const galleries = await uploaderService.fetchAvailableGalleries(getters['picaServiceId'])
        commit('setAvailableGalleries', galleries)
    },
    async updateImageResolution({commit}, {img, file}) {
        file.image = img
        const resolution = await fileUtils.getResolution(file)
        commit('updateImageResolution', {
            file: file,
            resolution: resolution.resolution
        })
    },
    async validatePicaCode({getters}, picaCode) {
        return await uploaderService.validatePicaCode(getters['picaServiceId'], picaCode)
    },
    async fetchStats({commit, getters}) {
        const stats = await uploaderService.fetchStats(getters['picaServiceId'])
        commit('setStats', stats)
    },
    updateStats({commit}, {processed, uploaded}) {
        if (processed) commit('setProcessed', processed)
        if (uploaded) commit('setUploaded', uploaded)
    }
})

export default uploaderService => ({
    namespaced: true,
    state,
    getters,
    mutations,
    actions: actions(uploaderService)
})

